// useLiveWalletBalance — realtime TON balance for one address.
//
// Bootstrap: ONE GET /v1/wallets/:address/live (also the tracker heartbeat —
// the backend registers the address in the O(1) tracked set). Realtime: the
// ws-gateway "wallet:{address}" channel pushes a new balance on every applied
// tx touching the address — no polling, no per-event getters anywhere.
//
// Ordering. Both paths carry `last_lt`, the logical time of the last applied
// transaction: strictly increasing per account, and the only sound key for
// merging them. The backend publishes to NATS *before* it mirrors the value
// into Valkey, so a bootstrap landing inside that window answers with a balance
// the stream already moved past — applying it would visibly roll the balance
// back. A snapshot is therefore accepted only when it is at least as new as
// what we already applied. (`as_of` cannot do this job: Go renders RFC3339Nano
// with trailing zeros stripped, so ".5Z" sorts above ".50001Z" as a string.)

import { useCallback, useEffect, useRef, useState } from 'react'
import { type LiveBalancePreview, fetchLiveBalancePreview } from '../api/userland'
import { displayDiagEnabled, noteDisplayEvent } from '../lib/displayWriteDiag'
import { formatTradeRawAmount } from '../lib/format'
import {
  figureKey,
  readLastKnownFigure,
  resetLastKnownFiguresForTest,
  writeLastKnownFigure,
} from '../lib/lastKnownFigure'
import { bindNativeSpendByExternal, noteShownNativeBalance } from '../lib/nativeSpendReserve'
import {
  armSettledBalanceTarget,
  clearSettledBalanceFloor,
  clearSettledBalanceTarget,
  holdsBalances,
  isSettledBalance,
  pendingTradeOwner,
  settledBalanceFloorMs,
  subscribePendingTrade,
} from '../lib/pendingTradeGate'
import { waitForSubscriptionBarrier } from '../lib/subscriptionBarrier'
import { canonicalTonAddress } from '../lib/tonAddress'
import { noteTradeLifecycleBalancePush } from '../lib/tradeLifecycleTelemetry'
import { useWsOptional } from '../ws/WsContext'
import type { PortfolioSeed } from './usePortfolioSeed'
import { useWsSubscription } from './useWsSubscription'

// Re-announce viewer interest before the tracker's 10-minute TTL lapses.
const HEARTBEAT_MS = 4 * 60 * 1000

// A wallet the balance table has no row for yet answers "unknown" (/live 404,
// fetchLiveBalancePreview null): the first /live is what tells the observer,
// which tracks and reads the wallet within about 2 s and pushes on
// wallet:{address}. The first /live leaves AT ONCE, in parallel with the
// subscription (a known wallet shows its figure without waiting for anything).
// An unknown answer waits for the push and, once the server ACKed the
// subscription, asks /live ONE more time: a push that left before the
// subscription was in place is gone, but the row it was made from is already
// in the table by then, so that one ask finds it; a push after the ACK
// arrives on the subscription. The ACK wait is bounded by SUBSCRIBE_WAIT_MS so
// a socket that never ACKs still gets its one more ask.
const SUBSCRIBE_WAIT_MS = 1500

type RememberedLiveBalance = {
  balanceNano: string
  holdingsCount: number | null
  asOf: string | null
}

const rememberedLive = new Map<string, RememberedLiveBalance>()

export function resetLiveBalanceMemoryForTest(): void {
  rememberedLive.clear()
  resetLastKnownFiguresForTest()
}

// Module memory dies with the tab, and the tab is exactly where the wallet's
// real balance was known a second ago. Persist it per wallet so a reload
// paints the real number instead of a zero (header) or an em-dash (rows).
function rememberLive(address: string, value: RememberedLiveBalance): void {
  rememberedLive.set(address, value)
  writeLastKnownFigure(figureKey('live', address), JSON.stringify(value))
}

function rememberedFor(address: string | null): Omit<LiveWalletBalance, 'reload'> {
  const unknown = { balanceNano: null, holdingsCount: null, asOf: null }
  if (!address) return unknown
  const remembered = rememberedLive.get(address)
  if (remembered) return remembered
  const stored = readLastKnownFigure(figureKey('live', address))
  if (!stored) return unknown
  try {
    const parsed = JSON.parse(stored) as RememberedLiveBalance
    if (typeof parsed?.balanceNano !== 'string') return unknown
    rememberedLive.set(address, parsed)
    return parsed
  } catch {
    return unknown
  }
}

export type LiveWalletBalance = {
  // null until the first bootstrap answer (or when the backend is off).
  balanceNano: string | null
  holdingsCount: number | null
  asOf: string | null
  reload: () => void
}

type WalletBalanceEvent = {
  address: string
  balance_nano: string
  last_lt: string
  as_of: string
  reason: string
}

/** The fields of a wallet.confirmation push this hook reads. */
type WalletConfirmationEvent = {
  status: string
  in_msg_hash: string
  account: string
  lt: string
}

// The in-flight GRAM ledger hears its trades' confirmations here: this hook
// lives as long as the tab, the executor only as long as the pair route (a
// route change orphaned its tickets until the TTL, 2026-11-03).
function noteConfirmation(wallet: string | null, c: WalletConfirmationEvent | undefined): void {
  if (!wallet || c?.status !== 'finalized' || canonicalTonAddress(c.account) !== wallet) return
  bindNativeSpendByExternal(wallet, c.in_msg_hash, c.lt)
}

// readLt extracts a usable lt, or null when the payload carries none (an API
// older than the last_lt field).
function readLt(value: unknown): bigint | null {
  if (typeof value === 'string' && /^\d+$/.test(value)) return BigInt(value)
  // Rolling compatibility only. A numeric LT is accepted when it is still
  // exactly representable; unsafe JSON numbers remain unorderable/fail-closed.
  if (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0) return BigInt(value)
  return null
}

// acceptsLt reports whether an update at `incoming` lt may overwrite the
// balance applied at `applied` lt. Nothing applied yet → take it. Unknown
// incoming lt against an applied one → refuse: an unorderable value must never
// roll a known-good balance back.
export function acceptsLt(applied: bigint | null, incoming: bigint | null): boolean {
  if (applied === null) return true
  if (incoming === null) return false
  return incoming >= applied
}

export function useLiveWalletBalance(
  address: string | null,
  seed?: PortfolioSeed | null
): LiveWalletBalance {
  const [reloadGeneration, setReloadGeneration] = useState(0)
  const canonicalAddress = canonicalTonAddress(address)
  const [state, setState] = useState<Omit<LiveWalletBalance, 'reload'>>(() =>
    rememberedFor(canonicalAddress)
  )
  // The balance on screen, readable from a subscription callback that must not
  // re-arm itself on every render.
  const stateRef = useRef(state)
  stateRef.current = state
  // lt of the balance ON SCREEN (appliedLt below also moves for held values).
  // The in-flight GRAM ledger ends a trade's reservation only once a balance
  // that contains its send is the one the guard reads.
  const shownLt = useRef<bigint | null>(null)
  useEffect(() => {
    noteShownNativeBalance(canonicalAddress, state.balanceNano, shownLt.current, state.asOf)
  }, [canonicalAddress, state.balanceNano, state.asOf])
  useEffect(() => {
    if (!canonicalAddress || state.balanceNano === null) return
    rememberLive(canonicalAddress, {
      balanceNano: state.balanceNano,
      holdingsCount: state.holdingsCount,
      asOf: state.asOf,
    })
  }, [canonicalAddress, state.asOf, state.balanceNano, state.holdingsCount])
  // lt of the balance currently on screen. Mutated only from event handlers —
  // never inside a setState updater, which StrictMode runs twice.
  const appliedLt = useRef<bigint | null>(null)
  // A trade is two chain events. The wallet's own transaction is confirmed
  // first, so the balance moves while the swap is still executing — measured on
  // a real sell, the balance changed 1150 ms before the swap's row reached the
  // browser, showing an in-flight value that corrected itself a second later.
  // While a trade is in flight the newest balance is held here and released the
  // moment the trade resolves, so it lands with the toast and the position
  // instead of ahead of them. The held value is the real one; only its timing
  // changes.
  const heldBalance = useRef<{ balanceNano: string; asOf: string; lt: bigint | null } | null>(null)
  const shownAddress = useRef<string | null>(null)
  // Seed mode: the page composed the bootstrap into ONE /seed fetch, so this
  // hook skips its own initial /live read per address and applies the seed's
  // live section instead. The tracker heartbeat and manual reloads keep
  // hitting the standalone endpoint, unchanged. `undefined` = legacy
  // standalone mode (other callers); null = seed mode, seed still pending.
  const seeded = seed !== undefined
  const seedBootstrapFor = useRef<string | null>(null)
  const ws = useWsOptional()
  // The bootstrap effect's one-more-ask, reachable from the seed effect: a seed
  // whose live section is unknown asks /live again like a standalone answer does.
  const unknownRetry = useRef<(() => void) | null>(null)

  // ONE admission gate for both balance sources. The WS push and the REST
  // snapshot describe the same number, but the confirm-time reload used to
  // apply its snapshot directly while the push path held it — between the
  // send block and the swap block the mirror still serves the send state, so
  // the balance landed on a mid-trade value at the toast and corrected itself
  // a moment later (stage-2 stand, S1: bootstrap 8.6 at the toast, push 8.985
  // 100 ms after). Every write now passes the same pending/floor/settled
  // checks; only the lt-ordering guard stays at the callers, because the two
  // sources disagree about what a refusal must still update.
  const admitBalance = useCallback(
    (balanceNano: string, asOf: string, lt: bigint | null, writer: string): void => {
      if (lt !== null) appliedLt.current = lt
      // The gate and its post-trade filters belong to the TRADING wallet.
      // Another wallet's hook instance (a portfolio row, a wallet switched to
      // mid-trade) must neither be held by them nor consume them.
      const owner = pendingTradeOwner()
      if (owner !== null && owner !== shownAddress.current) {
        if (displayDiagEnabled())
          noteDisplayEvent('balance', 'gram-balance', balanceNano, writer, 'applied-not-owner')
        shownLt.current = lt
        setState((prev) => ({ balanceNano, holdingsCount: prev.holdingsCount, asOf }))
        return
      }
      // The gate holds OVERWRITES mid-trade; a first paint is not one. An
      // instance mounting while a trade is in flight (navigation to a pair
      // page) must show the last known balance, not "—" until the trade
      // resolves (review finding).
      if (stateRef.current.balanceNano === null) {
        if (displayDiagEnabled())
          noteDisplayEvent('balance', 'gram-balance', balanceNano, writer, 'applied-first-paint')
        shownLt.current = lt
        setState((prev) => ({ balanceNano, holdingsCount: prev.holdingsCount, asOf }))
        return
      }
      if (holdsBalances()) {
        // ONE trade in flight: keep the newest, show it when the trade
        // resolves (its send -> proceeds). An overlapping series holds nothing.
        heldBalance.current = { balanceNano, asOf, lt }
        if (displayDiagEnabled())
          noteDisplayEvent('balance', 'push', balanceNano, writer, 'held-gate-open')
        return
      }
      // A trade moves the wallet more than once: the send leaves first, the
      // proceeds arrive after the swap. The floor is the trade's own chain
      // second — a balance older than the trade describes the wallet
      // mid-transaction and is not what the operator asked to see.
      const floorMs = settledBalanceFloorMs()
      if (floorMs > 0) {
        const asOfMs = Date.parse(asOf)
        if (Number.isFinite(asOfMs) && asOfMs < floorMs) {
          heldBalance.current = { balanceNano, asOf, lt }
          if (displayDiagEnabled())
            noteDisplayEvent('balance', 'push', balanceNano, writer, 'held-pre-trade-floor')
          return
        }
        clearSettledBalanceFloor()
      }
      // A transaction is several events and each carries its own balance; only
      // the END of the transaction is a number to show. The emulator measured
      // that end before signing, so the last event is recognisable.
      if (!isSettledBalance(balanceNano)) {
        heldBalance.current = { balanceNano, asOf, lt }
        if (displayDiagEnabled())
          noteDisplayEvent('balance', 'push', balanceNano, writer, 'held-not-settled')
        return
      }
      clearSettledBalanceTarget()
      // An applied value supersedes anything held: the lt guard proved it is
      // the newest, and a stale held value released at the NEXT gate
      // transition would roll the display back.
      heldBalance.current = null
      if (displayDiagEnabled())
        noteDisplayEvent('balance', 'gram-balance', balanceNano, writer, 'applied')
      shownLt.current = lt
      setState((prev) => ({
        balanceNano,
        holdingsCount: prev.holdingsCount,
        asOf,
      }))
    },
    []
  )

  // applyPreview funnels every REST snapshot — standalone bootstrap/heartbeat
  // or the seed's live section — through the lt-ordering guard and then the
  // SAME admission gate as a push.
  const applyPreview = useCallback(
    (p: LiveBalancePreview): void => {
      const lt = readLt(p.last_lt)
      // holdings_count is projected from Postgres positions, not from the
      // balance stream, so it is always the freshest value we have.
      setState((prev) => ({ ...prev, holdingsCount: p.holdings_count }))
      if (!acceptsLt(appliedLt.current, lt)) {
        if (displayDiagEnabled())
          noteDisplayEvent(
            'balance',
            'bootstrap',
            p.balance_nano,
            'rest-snapshot',
            'refused-lt-order'
          )
        return
      }
      admitBalance(p.balance_nano, p.as_of, lt, 'rest-snapshot')
    },
    [admitBalance]
  )

  // Declared BEFORE the bootstrap effect on purpose: React runs passive effects in
  // declaration order, so the SUBSCRIBE leaves just before the first /live and the
  // two travel in parallel (the first /live does not wait for the ACK).
  useWsSubscription<WalletBalanceEvent>(
    canonicalAddress ? `wallet:${canonicalAddress}` : null,
    (msg) => {
      if (msg.event === 'wallet.confirmation') {
        noteConfirmation(canonicalAddress, msg.data as unknown as WalletConfirmationEvent)
        return
      }
      if (msg.event !== 'wallet.balance') return
      const data = msg.data
      if (!data || data.address !== canonicalAddress) return
      const lt = readLt(data.last_lt)
      // Recorded BEFORE the guard rules, and it never influences the ruling.
      // The balance is the last lane still trailing the tape row, and a push
      // that arrived late is indistinguishable from one we refused unless both
      // are written down.
      const accepted = acceptsLt(appliedLt.current, lt)
      noteTradeLifecycleBalancePush(lt === null ? '' : lt.toString(), accepted)
      if (!accepted) {
        if (displayDiagEnabled())
          noteDisplayEvent('balance', 'push', data.balance_nano, 'ws-push', 'refused-lt-order')
        return
      }
      admitBalance(data.balance_nano, data.as_of, lt, 'ws-push')
    }
  )

  useEffect(() => {
    void reloadGeneration
    const addressChanged = shownAddress.current !== canonicalAddress
    shownAddress.current = canonicalAddress
    if (addressChanged) {
      shownLt.current = null
      setState(rememberedFor(canonicalAddress))
      appliedLt.current = null
    }
    if (!canonicalAddress) return
    let alive = true
    let retried = false
    let ackTimer: ReturnType<typeof setTimeout> | null = null
    const abort = new AbortController()

    // After an unknown answer: wait for the server's ACK of wallet:{address}
    // (bounded; no socket = at once), then ask /live once more, unless a push or
    // a snapshot was applied meanwhile.
    const scheduleRetry = () => {
      if (retried || !alive) return
      retried = true
      let pending = true
      const ask = () => {
        if (!pending) return
        pending = false
        if (ackTimer !== null) clearTimeout(ackTimer)
        ackTimer = null
        if (!alive || appliedLt.current !== null) return
        bootstrap(false)
      }
      ackTimer = setTimeout(ask, SUBSCRIBE_WAIT_MS)
      waitForSubscriptionBarrier(ws, [`wallet:${canonicalAddress}`], abort.signal).then(ask, ask)
    }
    const bootstrap = (retryUnknown: boolean) => {
      void fetchLiveBalancePreview(canonicalAddress).then((p: LiveBalancePreview | null) => {
        if (!alive) return
        if (p === null) {
          if (retryUnknown) scheduleRetry()
          return
        }
        applyPreview(p)
      })
    }
    unknownRetry.current = scheduleRetry

    // In seed mode the composed /seed fetch carries this address's first
    // snapshot; only later bootstraps (heartbeat, manual reload, and any
    // reload racing the seed) hit the standalone endpoint.
    if (!seeded || seedBootstrapFor.current === canonicalAddress) {
      bootstrap(true)
    } else {
      seedBootstrapFor.current = canonicalAddress
    }
    const heartbeat = setInterval(() => bootstrap(true), HEARTBEAT_MS)
    return () => {
      alive = false
      abort.abort()
      clearInterval(heartbeat)
      if (ackTimer !== null) clearTimeout(ackTimer)
      if (unknownRetry.current === scheduleRetry) unknownRetry.current = null
    }
  }, [canonicalAddress, reloadGeneration, seeded, applyPreview, ws])

  // Applies the composed seed's live section through the same lt guard as a
  // standalone bootstrap. A seed for another address (previous wallet) is
  // ignored; the current wallet's seed arrives as a new object.
  useEffect(() => {
    if (!seed || seed.status !== 'done' || !canonicalAddress) return
    if (canonicalTonAddress(seed.address) !== canonicalAddress) return
    if (seed.live === null) {
      // The seed's live section is unknown (the wallet has no row yet): wait for
      // the push, and ask /live once more once the subscription is ACKed.
      unknownRetry.current?.()
      return
    }
    applyPreview(seed.live)
  }, [seed, canonicalAddress, applyPreview])

  // Release the held balance the instant the trade resolves — confirmed,
  // failed, or the gate's own hold expiring. Never drops it.
  useEffect(
    () =>
      subscribePendingTrade(() => {
        if (holdsBalances()) {
          // The trade just started. The OWNER's instance is the one that knows
          // the wallet's balance BEFORE it — another wallet's instance arming
          // here would poison the target with an unrelated balance.
          const owner = pendingTradeOwner()
          if (owner === null || owner === shownAddress.current) {
            armSettledBalanceTarget(stateRef.current.balanceNano)
          }
          return
        }
        // The trade resolved, or a second trade overlapped it (the series
        // then holds nothing). The computed target is NEVER painted: it only
        // tells the real balances apart (operator 2026-11-02 — it put
        // base + Σdeltas on screen over a real push for ~5 s).
        const held = heldBalance.current
        if (!held) return
        // A held value older than what is already applied must never be
        // released — that is a rollback, not a late update.
        if (held.lt !== null && appliedLt.current !== null && held.lt < appliedLt.current) {
          heldBalance.current = null
          return
        }
        // Do not release a balance older than the trade itself — that is the
        // wallet mid-transaction, and showing it is the wobble. The next push
        // that reaches the trade's chain second releases instead.
        const floorMs = settledBalanceFloorMs()
        if (floorMs > 0) {
          const asOfMs = Date.parse(held.asOf)
          if (Number.isFinite(asOfMs) && asOfMs < floorMs) return
          clearSettledBalanceFloor()
        }
        // Same rule on release: a mid-transaction balance is not the outcome.
        if (!isSettledBalance(held.balanceNano)) return
        clearSettledBalanceTarget()
        heldBalance.current = null
        if (displayDiagEnabled())
          noteDisplayEvent(
            'balance',
            'gram-balance',
            held.balanceNano,
            'held-release',
            'trade-resolved'
          )
        shownLt.current = held.lt
        setState((prev) => ({
          balanceNano: held.balanceNano,
          holdingsCount: prev.holdingsCount,
          asOf: held.asOf,
        }))
      }),
    []
  )

  return {
    ...state,
    reload: useCallback(() => setReloadGeneration((generation) => generation + 1), []),
  }
}

// formatTon renders nanotons for wallet rows: honest em-dash before data.
export function formatTon(balanceNano: string | null): string {
  if (balanceNano === null) return '—'
  return formatTradeRawAmount(balanceNano, 9)
}

const rememberedHeaderTon = { text: null as string | null }

/** Header chip: never flash 0 → em-dash → real. Keep the last known figure
 *  until a live nanotons string arrives, and report UNKNOWN as the empty
 *  string — a wallet we have not measured yet does not have a zero balance,
 *  and saying so is a claim the user acts on. The chip renders a skeleton for
 *  it (see lastKnownFigure.ts for the rule this belongs to). */
export function formatHeaderTon(balanceNano: string | null): string {
  if (balanceNano !== null && /^(0|[1-9]\d*)$/.test(balanceNano)) {
    const text = formatTradeRawAmount(balanceNano, 9)
    if (text !== '—') {
      rememberedHeaderTon.text = text
      return text
    }
  }
  return rememberedHeaderTon.text ?? ''
}

export function resetHeaderTonMemoryForTest(): void {
  rememberedHeaderTon.text = null
}

export function sumBalanceNanos(values: Array<string | null>): string | null {
  if (values.length === 0) return '0'
  if (values.some((value) => value === null)) return null
  let total = 0n
  for (const value of values) {
    try {
      total += BigInt(value as string)
    } catch {
      return null
    }
  }
  return total.toString()
}
